export const _Q_qToken = `X2X2X_X2X2X`;
export const _Q_qToken2 = `XXX_XXX2`;
export const _Q_qTokenV = `VXVX_XVXV`;
export const _Q_qTokenPage = `PXPX_PXPX`;

/**
 * A machine id, made safe to stand inside a GraphQL NAME.
 * ---------------------------------------------------------------------------
 * _Q_qToken2 is spliced into operation names and aliases - `query
 * hostData_<id>_v0` - and a GraphQL name may only hold letters, digits and
 * underscores. A scanned machine's id is hex with dashes, so turning the
 * dashes into underscores was enough and that is what every call site does.
 *
 * It is not enough for a machine nobody scanned. Those are inferred from the
 * far end of somebody else's connection and identified by address, so their
 * id is `142.251.27.95`, or `::ffff:10.130.0.12` - and a dot or a colon in an
 * operation name is a query the server never sees, because it fails to parse
 * in the browser: "Syntax Error: Expected {, found :".
 *
 * So: anything that is not a letter, a digit or an underscore becomes one.
 * For an id that was already safe this is exactly what replacing the dashes
 * did, which is why it can be dropped in where that was.
 */
export const qName = (id) => String(id ?? "").replace(/[^0-9A-Za-z_]/g, "_");
export const maxPageSize = 1000;

export const _Q_dashboards = `
dashboards{
  totalSystems
  totalHosts
  hostsByOs {
    count
    version
  }
  hostsByJava {
    count
    version
  }
  totalApplications
  totalInstalledPackages
  candidates
  totalEol
  totalHostsWithVulnerabilities
}
`;

export const _Q_getHostsInstalledPackagesAgg = `
  getHostsInstalledPackagesAgg{
    machineId
    totalInstalled
    eolPackages
    candidatePackages
  }
`;

export const _Q_getScannedByDayAndOs = `
  getScannedByDayAndOs{
    collectionDay,
    hosts{
      cnt,
      osName
    }        
  }
`;

export const _Q_getIpNotScanned = `
        getIpNotScanned{
            ip
            numberOfConnections
        }
`;

export const _Q_systemsDiscovery = `
{
  systems(page: 1, pageSize: ${maxPageSize})  {
  systems {
    id
    collectedAt
    version
    isVirtual
    uptime
    boottime
    hostName
    cloudType
    instanceId
    nics {
      name
      ips {
        ip
        isIpV6
      }
      macAddress
    }
    disks {
      diskType
      name
      mountPoint
      totalSpace
      availableSpace
      isRemovable
      fileSystemType
    }
    processor {
      frequency
      vendorId
      manufacturer
      cpuUsage
      cores {
        name
        frequency
        cpuUsage
      }
    }
    memory {
      timestamp
      available
      used
      total
      free
      freeSwap
      usedSwap
      totalSwap
    }
    users {
      uid
      gid
      name
      groups
    }
    gpus {
      name
      vendor
      capacity
    }
    bios{
      biosInfo{
        vendor
        version
        releaseDate
      }
      systemInfo{
        manufacturer
        productName
        version
        serialNumber
        wakeupType
        skuNumber
        family
      }
      baseBoardInfo{
        manufacturer
        product
        version
        serialNumber
        assetTag
      }
    }
  }
}}
`;

export const _Q_hostsDiscovery = `
{
  hosts(page: 1, pageSize: ${maxPageSize}) {
    hosts{
      timestamp
      machineId
      name
      description
      owner
      tags
      protocolId
      scannedIp
      os {
        name
        version
        kernelName
        kernelVersion
        windowsLicenseInfo{
          name
          description
          activationId
          applicationId
          extendedPid
          productKeyChannel
          installationId
          useLicenseUrl
          validationUrl
          partialProductKey
          licenseStatus
          timebasedActivationExpiration
          remainingWindowsRearmCount
          remainingSkuRearmCount
          trustedTime
        }
        windowsInstalledUpdates {
          caption
          csName
          description
          fixComments
          hotFixId
          installDate
          installedBy
          installedOn
          name
          servicePackInEffect
          status
        }
        windowsDetails {
          edition
          buildNumber
          systemDirectory
          installDate
          lastBootUpTime
          maxNumberOfProcesses
          maxProcessMemorySize
          numberOfProcesses
          osArchitecture
        }
        isEol
        eolDate
        latestVersion
      }
      jvm {
        name
        version
        javaHome
        isEol
        eolDate
        latestVersion
      }
      fs {
        name
        mountPoint
        fileSystemType
      }
    } 
  }
}
`;

export const _Q_getHostsWithVulnerabilities = `
  getHostsWithVulnerabilities {
    machineId
    severityCounts {
      critical
      high
      medium
      low
      unknown
    }
  }
`;


export const _Q_getHostWithVulnerabilities = `
    getHostWithVulnerabilities(machineId: "${_Q_qToken}"){
      machineId
      vulnerabilities {
        version
        packageName
        vulnerability
      }
    }
`;


export const _Q_getApplicationGraph = `
query getApplicationGraph${_Q_qToken2}{
  getApplicationGraph(machineId: "${_Q_qToken}"){
    apps {
      id
      applicationTemplateId
      machineId
      name
      version
      machineEntityVersion
      binFolder
      description
      collectedDataPath
      cmdLine
      properties
      protocolId
      dockerImageUrl
      forgeRunId
    }
    hosts {
      collectedAt
      machineId
      name
      description
    }
    relations {
      id
      machineId
      connectionState
      appId
      localIp
      localPort
      remoteIp
      remotePort
      remoteAppId
    }
    appToHost {
      appId
      hostId
    }
  }
}
`

export const _Q_installedPackages = `
query installedPackages${_Q_qToken2}{
  installedPackages(machineId: "${_Q_qToken}"){
    name
    version
    candidateVersion
    isEol
    eolDate
    vulnerabilities
    vendor
  }
}`;


export const _Q_installedPackagesMultiple = `
  installedPackages(machineId: "${_Q_qToken}"){
    name
    version
    candidateVersion
    isEol
    eolDate
    vulnerabilities
    vendor
  }
`;

export const _Q_getAllContainers = `
getAllContainers{
    id
    containerType
    items
    entityVersion
    createdAt
    systemId
}`;

export const _Q_getAppConnections = `
  {
    getAppConnections {
    id
    machineId
    appId
    localIp
    localPort
    remoteIp
    remotePort
    remoteAppId
  }
}
`;

export const _Q_getResolvedAppConnections = _Q_getAppConnections.replace("getAppConnections", "getResolvedAppConnections");


export const _Q_getAllVersionTimePaged = `
query getVersionTime($page: Int, $pageSize: Int) {
  systems(page: $page, pageSize: $pageSize)  {
     systems{
      id
      version
     }
     pageInfo {
      totalPages
      currentPage
    }
  }

  hosts(page: $page, pageSize: $pageSize) {
      hosts{
        name
        machineId
        timestamp
      }
      pageInfo {
       totalPages
       currentPage
     }
    }
}`;



export const _Q_VersionTime = {
  query: `${_Q_getAllVersionTimePaged}`,
  name: "versionTime"
}



export const _Q_getAllApplicationsPaged = `
getCurrentApplications(page: $page, pageSize: $pageSize) {
    apps {
        id
        applicationTemplateId
        machineId
        name
        version
        machineEntityVersion
        binFolder
        description
        collectedDataPath
        cmdLine
        properties
        dockerImageUrl
        forgeRunId
        connectors {
            connectorType
            name
            values
        }
        protocolId
        scannedIp
        tags
        extracted
        downloadUrl
        extractedAt
    }
    pageInfo {
        totalPages
        currentPage
    }
}
`;

export const _Q_getAllApplicationComponents = `
getApplicationComponents {
  id
  name
  description
  repositoryUrl
  dockerImageUrl
  forgeRunId
  repositoryType
  token
  projectId
  applicationInstanceId
  downloadUrl
  uploadedAt
  createdAt
  forgeRunId
}
`

export const _Q_MergedQueryApps = {
  query: `${_Q_getAllApplicationsPaged}`,
  mergeWithQuery: `${_Q_getAllApplicationComponents}`,
  name: "getCurrentApplicationsPaged",
  mergeName:"getAllApplicationComponents"
}


export const _Q_getAllApplicationsPagedCOMP = `
  getCurrentApplications(page: $page, pageSize: $pageSize) {
    apps {
      id
      applicationTemplateId
      machineId
      name
      version
      machineEntityVersion
      binFolder
      description
      collectedDataPath
      cmdLine
      properties
      dockerImageUrl
      forgeRunId
      connectors {
        connectorType
        name
        values
      }
      protocolId
      scannedIp
      tags
      extracted
      downloadUrl
      extractedAt
    }
    pageInfo {
      totalPages
      currentPage
    }
  }
  getApplicationComponents {
    id
    name
    description
    repositoryUrl
    dockerImageUrl
    forgeRunId
    repositoryType
    projectId
    applicationInstanceId
    downloadUrl
    uploadedAt
    createdAt
  }
`;

export const _Q_getAllApplications = `
  getCurrentApplications(page: 1, pageSize: ${maxPageSize}) {
    apps {
      id
      applicationTemplateId
      machineId
      name
      version
      machineEntityVersion
      binFolder
      description
      collectedDataPath
      cmdLine
      dockerImageUrl
      forgeRunId
      properties
      connectors {
        connectorType
        name
        values
      }
      scannedIp
      protocolId
      tags
          extracted
          extractedAt
    }
    pageInfo {
      totalPages
      currentPage
    }
  }
  getApplicationComponents {
    id
    name
    description
    repositoryUrl
    dockerImageUrl
    forgeRunId
    repositoryType
    projectId
    applicationInstanceId
    downloadUrl
    uploadedAt
    createdAt
  }
`;

export const _Q_getCurrentApplicationsForMachine = _Q_getAllApplications.replace("getCurrentApplications {", `getCurrentApplicationsForMachine(machineId: "${_Q_qToken}") {`);
export const _Q_getCurrentApplicationsForMachineShort = _Q_getCurrentApplicationsForMachine.replace(`query getCurrentApplications_${_Q_qToken2}{`, "").replace("cmdLine", "").slice(0, -1);

export const _Q_systemV = `
query systemData_${_Q_qToken2}_v${_Q_qTokenV}{
  getSystemByVersion(machineId: "${_Q_qToken}", version: ${_Q_qTokenV} ){
    id
    collectedAt
    version
    isVirtual
    uptime
    boottime
    hostName
    cloudType
    instanceId
    nics {
      name
      ips {
        ip
        isIpV6
      }
      macAddress
    }
    disks {
      diskType
      name
      mountPoint
      totalSpace
      availableSpace
      isRemovable
      fileSystemType
    }
    processor {
      frequency
      vendorId
      manufacturer
      cpuUsage
      cores {
        name
        frequency
        cpuUsage
      }
    }
    memory {
      timestamp
      available
      used
      total
      free
      freeSwap
      usedSwap
      totalSwap
    }
    users {
      uid
      gid
      name
      groups
    }
    gpus {
      name
      vendor
      capacity
    }
    bios{
      biosInfo{
        vendor
        version
        releaseDate
      }
      systemInfo{
        manufacturer
        productName
        version
        serialNumber
        wakeupType
        skuNumber
        family
      }
      baseBoardInfo{
        manufacturer
        product
        version
        serialNumber
        assetTag
      }
    }

  }
}
`;

export const _Q_system = `
query systemData_${_Q_qToken2}{
  system(machineId: "${_Q_qToken}"){
    id
    collectedAt
    version
    isVirtual
    uptime
    boottime
    hostName
    cloudType
    instanceId
    nics {
      name
      ips {
        ip
        isIpV6
      }
      macAddress
    }
    disks {
      diskType
      name
      mountPoint
      totalSpace
      availableSpace
      isRemovable
      fileSystemType
    }
    processor {
      frequency
      vendorId
      manufacturer
      cpuUsage
      cores {
        name
        frequency
        cpuUsage
      }
    }
    memory {
      timestamp
      available
      used
      total
      free
      freeSwap
      usedSwap
      totalSwap
    }
    users {
      uid
      gid
      name
      groups
    }
    gpus {
      name
      vendor
      capacity
    }
    bios{
      biosInfo{
        vendor
        version
        releaseDate
      }
      systemInfo{
        manufacturer
        productName
        version
        serialNumber
        wakeupType
        skuNumber
        family
      }
      baseBoardInfo{
        manufacturer
        product
        version
        serialNumber
        assetTag
      }
    }
  }
}
`;

export const _Q_versionTime = `
  query versionTime{
    systems(page: 1, pageSize: ${maxPageSize}){systems{ id, version }}
    hosts(page: 1, pageSize: ${maxPageSize}){ hosts{name, machineId, timestamp }} 
  }
`;

export const _Q_hostV = `
query hostData_${_Q_qToken2}_v${_Q_qTokenV}{
  hostByVersion(machineId: "${_Q_qToken}", version: ${_Q_qTokenV}){
      timestamp
      machineId
      name
      description
      owner
      tags
      protocolId
      scannedIp
      os {
        name
        version
        kernelName
        kernelVersion
        windowsLicenseInfo{
          name
          description
          activationId
          applicationId
          extendedPid
          productKeyChannel
          installationId
          useLicenseUrl
          validationUrl
          partialProductKey
          licenseStatus
          timebasedActivationExpiration
          remainingWindowsRearmCount
          remainingSkuRearmCount
          trustedTime
        }    
        windowsInstalledUpdates {
          caption
          csName
          description
          fixComments
          hotFixId
          installDate
          installedBy
          installedOn
          name
          servicePackInEffect
          status
        }
        windowsDetails {
          edition
          buildNumber
          systemDirectory
          installDate
          lastBootUpTime
          maxNumberOfProcesses
          maxProcessMemorySize
          numberOfProcesses
          osArchitecture
        }
        isEol
        eolDate
        latestVersion
      }
      jvm {
        name
        version
        javaHome
        isEol
        eolDate
        latestVersion
      }
      fs {
        name
        mountPoint
        fileSystemType
      }
    } 
}
`;

export const _Q_host = `
query hostData_${_Q_qToken2}{
  host(machineId: "${_Q_qToken}"){
      timestamp
      machineId
      name
      description
      owner
      tags
      os {
        name
        version
        kernelName
        kernelVersion
        windowsLicenseInfo{
          name
          description
          activationId
          applicationId
          extendedPid
          productKeyChannel
          installationId
          useLicenseUrl
          validationUrl
          partialProductKey
          licenseStatus
          timebasedActivationExpiration
          remainingWindowsRearmCount
          remainingSkuRearmCount
          trustedTime
        }    
        windowsInstalledUpdates {
          caption
          csName
          description
          fixComments
          hotFixId
          installDate
          installedBy
          installedOn
          name
          servicePackInEffect
          status
        }
        windowsDetails {
          edition
          buildNumber
          systemDirectory
          installDate
          lastBootUpTime
          maxNumberOfProcesses
          maxProcessMemorySize
          numberOfProcesses
          osArchitecture
        }
        isEol
        eolDate
        latestVersion
      }
      jvm {
        name
        version
        javaHome
        isEol
        eolDate
        latestVersion
      }
      fs {
        name
        mountPoint
        fileSystemType
      }
    } 
}
`;

export const _Q_queryProcesses = `
query qProcesses_${_Q_qToken2}{
  processesByVersion(machineId: "${_Q_qToken}", version: ${_Q_qTokenV}) {
    pid
    parentPid
    name
    cwd
    userId
    groupId
    cmdLine
    exePath
    rootPath
    status
    envVars
    cpuUsage
    memoryUsage
    virtualMemoryUsage
    diskUsage {
      totalWrittenBytes
      writtenBytes
      totalReadBytes
      readBytes
    }
    startTime
    runningTime
  }
}
`

export const _Q_queryConnections = `
query qconnections_${_Q_qToken2}{
  connectionsByVersion(machineId: "${_Q_qToken}", version: ${_Q_qTokenV}) {
    address {
      localAddr
      localPort
      remoteAddr
      remotePort
      state
      protocol
    }
    associatedPids
  }
}
`


export const _Q_queryFields =`
query queryFields {
  __type(name: "${_Q_qToken}") {
      ...FullType
  }
} 

fragment FullType on __Type {
    name
    fields(includeDeprecated: true) {
      name
      type {
        ...TypeRef
      }
    }
  }
  
  fragment TypeRef on __Type {
    kind
    name
    ofType {
      kind
      name
      ofType {
        kind
        name
        ofType {
          kind
          name
        }
      }
    }
  }

`;

export const userTokenID = "XXX_USER_ID_XXX";

export const sub_query_system = `
  subscription Subscribe {
    subscribe(name: "system") {
      name
      data
    }
  }
`;

export const sub_query_user = `
  subscription Subscribe {
    subscribe(name: "${userTokenID}") {
      name
      data
    }
  }
`;


//connection_state
export const _Q_applicationsGraph = `
query applicationsGraph{
    applicationsGraph {
      apps {
        id
        name
        version
        machineId
        description
      }
      connections {
        id
        machineId
        appId
        localIp
        localPort
        remoteIp
        remotePort
        remoteAppId
        projectId
        connectionState
      }
    }
}
`;

export const _Q_hostsGraph = `
query hostsGraph{
  hostsGraph {
      hosts {
        collectedAt
        machineId
        name
        description
        isUnknownHost
      }
      connections {
        source
        target
        connections{
        localAddr
        localPort
        remoteAddr
        remotePort
        state
        protocol
      }
    }
  }
}
`;

export const _Q_vulnerabilityBlast = `
query vulnerabilityBlast
  {
    vulnerabilities {
      vulnerability {
        cve
        description
        vendor
        package
        version
        severity
      }
      affectedHosts
    }
  }

`;

export const _Q_getEnrichmentResults = `
  query GetEnrichmentResults($page: Int, $pageSize: Int) {
    getEnrichmentResults(page: $page, pageSize: $pageSize) {
      results { id applicationId discoveryId appName hostname status correlationScore enrichedAt createdAt updatedAt record }
      pageInfo { totalPages currentPage }
    }
  }
`;

// NOTE: server-side casing/arg type unconfirmed — if the server differs
// (e.g. get_enrichment_result_history / application_id / String!), fix only this string.
export const _Q_getEnrichmentResultHistory = `
  query GetEnrichmentResultHistory($applicationId: ID!, $page: Int, $pageSize: Int) {
    getEnrichmentResultHistory(applicationId: $applicationId, page: $page, pageSize: $pageSize) {
      results { id applicationId discoveryId appName hostname status correlationScore enrichedAt createdAt updatedAt record }
      pageInfo { totalPages currentPage }
    }
  }
`;

// Flat, non-versioned, tenant-scoped tables — no _by_version variant, no
// current_version join (see docs/superpowers/specs/2026-07-21-cmdb-artifact-repositories-graphql-design.md).
export const _Q_getArtifactRepositories = `
  query GetArtifactRepositories($page: Int, $pageSize: Int, $artifactType: String, $repositoryType: String) {
    getArtifactRepositories(page: $page, pageSize: $pageSize, artifactType: $artifactType, repositoryType: $repositoryType) {
      results {
        id projectId environmentId artifactType artifactNamespace artifactId artifactVersion
        recordType repositoryType fullUrl shortUrl createdAt updatedAt
      }
      pageInfo { totalPages currentPage }
    }
  }
`;

export const _Q_getExternalCmdbRecords = `
  query GetExternalCmdbRecords($page: Int, $pageSize: Int, $recordType: String, $sort: SortInput) {
    getExternalCmdbRecords(page: $page, pageSize: $pageSize, recordType: $recordType, sort: $sort) {
      results {
        id projectId environmentId cmdbInstance recordType recordId name
        upstreamRecordId downstreamRecordId ipAddress url lastModifiedDate createdAt updatedAt
      }
      pageInfo { totalPages currentPage totalItems }
    }
  }
`;

export const _Q = {
  _Q_qToken,
  _Q_qToken2,
  _Q_qTokenV,
  qName,
  _Q_dashboards,
  _Q_queryProcesses,
  _Q_queryConnections,
  _Q_installedPackages,
  _Q_getAllContainers,
  _Q_getScannedByDayAndOs,
  _Q_getIpNotScanned,
  _Q_systemsDiscovery,
  _Q_hostsDiscovery,
  _Q_getHostsWithVulnerabilities,
  _Q_getHostWithVulnerabilities,
  _Q_getHostsInstalledPackagesAgg,
  _Q_getAppConnections,
  _Q_getResolvedAppConnections,
  _Q_getAllApplications,
  _Q_getApplicationGraph,
  _Q_getCurrentApplicationsForMachine,
  _Q_getCurrentApplicationsForMachineShort,
  _Q_host,
  _Q_hostV,
  _Q_system,
  _Q_systemV,
  _Q_versionTime,
  _Q_applicationsGraph,
  _Q_hostsGraph,
  _Q_vulnerabilityBlast,
  _Q_getEnrichmentResults,
  _Q_getEnrichmentResultHistory,
  _Q_getArtifactRepositories,
  _Q_getExternalCmdbRecords
}